const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const CHART_RANGE_ITEMS = [
  { key: "daily", label: "Daily", variant: "all" },
  { key: "weekly", label: "Weekly", variant: "upcoming" },
  { key: "monthly", label: "Monthly", variant: "ongoing" },
  { key: "yearly", label: "Yearly", variant: "completed" },
];

export function parseDateParts(iso) {
  const raw = String(iso ?? "").slice(0, 10);
  const [y, m, d] = raw.split("-").map(Number);
  return { year: y, month: m, day: d, raw };
}

function weekKey(iso) {
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "unknown";
  const thursday = new Date(d);
  thursday.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const yearStart = new Date(thursday.getFullYear(), 0, 4);
  const week =
    1 +
    Math.round(
      ((thursday.getTime() - yearStart.getTime()) / 86400000 - 3 + ((yearStart.getDay() + 6) % 7)) / 7,
    );
  return `${thursday.getFullYear()}-W${String(week).padStart(2, "0")}`;
}

function monthKey(iso) {
  const { year, month } = parseDateParts(iso);
  if (!year || !month) return "unknown";
  return `${year}-${String(month).padStart(2, "0")}`;
}

function yearKey(iso) {
  const { year } = parseDateParts(iso);
  return year ? String(year) : "unknown";
}

function formatBucketLabel(key, range) {
  if (range === "daily") {
    const { day, month } = parseDateParts(key);
    return day ? String(day) : key;
  }
  if (range === "weekly") {
    return key.replace(/^\d{4}-W/, "W");
  }
  if (range === "monthly") {
    const [, m] = key.split("-");
    const idx = Number(m) - 1;
    return MONTH_SHORT[idx] ?? key;
  }
  return key;
}

function weeksInMonth(year, month) {
  const daysInMonth = new Date(year, month, 0).getDate();
  const keys = new Set();
  for (let day = 1; day <= daysInMonth; day += 1) {
    const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    keys.add(weekKey(iso));
  }
  return [...keys].sort();
}

function bucketKeysForRange(rows, range, year, month) {
  if (range === "daily") {
    const daysInMonth = new Date(year, month, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    });
  }

  if (range === "weekly") {
    return weeksInMonth(year, month);
  }

  if (range === "monthly") {
    return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`);
  }

  if (range === "yearly") {
    const years = new Set([year - 2, year - 1, year]);
    for (const row of rows) {
      const y = parseDateParts(row.attendance_date).year;
      if (y) years.add(y);
    }
    return [...years].sort((a, b) => a - b).map(String);
  }

  return [];
}

function rowBucketKey(row, range) {
  const iso = row.attendance_date;
  if (!iso) return null;
  if (range === "daily") return String(iso).slice(0, 10);
  if (range === "weekly") return weekKey(iso);
  if (range === "monthly") return monthKey(iso);
  if (range === "yearly") return yearKey(iso);
  return null;
}

export function aggregatePresenceSeries(rows, range, { year, month }) {
  const keys = bucketKeysForRange(rows, range, year, month);
  const counts = Object.fromEntries(keys.map((k) => [k, 0]));

  for (const row of rows) {
    if (String(row.status ?? "").toUpperCase() !== "PRESENT") continue;
    const key = rowBucketKey(row, range);
    if (key && key in counts) counts[key] += 1;
    else if (key && range !== "daily") counts[key] = (counts[key] ?? 0) + 1;
  }

  const orderedKeys = range === "daily" ? keys : [...keys].sort();
  if (range !== "daily") {
    for (const k of Object.keys(counts)) {
      if (!orderedKeys.includes(k)) orderedKeys.push(k);
    }
    orderedKeys.sort();
  }

  return {
    labels: orderedKeys.map((k) => formatBucketLabel(k, range)),
    values: orderedKeys.map((k) => counts[k] ?? 0),
  };
}

export function aggregateStatusBreakdown(rows, range, ctx) {
  if (range === "daily") {
    const breakdown = {};
    for (const row of rows) {
      const status = String(row.status ?? "UNKNOWN").toUpperCase();
      breakdown[status] = (breakdown[status] ?? 0) + 1;
    }
    return breakdown;
  }

  const breakdown = {};
  for (const row of rows) {
    const key = rowBucketKey(row, range);
    if (!key) continue;
    const label = formatBucketLabel(key, range);
    breakdown[label] = (breakdown[label] ?? 0) + 1;
  }
  return breakdown;
}

export function aggregateWorkedSeries(rows, range, { year, month }) {
  const keys = bucketKeysForRange(rows, range, year, month);
  const totals = Object.fromEntries(keys.map((k) => [k, 0]));
  const counts = Object.fromEntries(keys.map((k) => [k, 0]));

  for (const row of rows) {
    const key = rowBucketKey(row, range);
    if (!key) continue;
    if (!(key in totals) && range !== "daily") {
      totals[key] = 0;
      counts[key] = 0;
    }
    if (key in totals) {
      totals[key] += Number(row.worked_minutes ?? 0);
      counts[key] += 1;
    }
  }

  const orderedKeys = range === "daily" ? keys : Object.keys(totals).sort();
  const values = orderedKeys.map((k) => {
    if (range === "daily") return totals[k] ?? 0;
    const c = counts[k] ?? 0;
    return c > 0 ? Math.round((totals[k] ?? 0) / c) : 0;
  });

  return {
    labels: orderedKeys.map((k) => formatBucketLabel(k, range)),
    values,
    latest: values[values.length - 1] ?? 0,
  };
}

export function chartRowsForRange(filteredMonthRows, filteredYearRows, range) {
  if (range === "monthly" || range === "yearly") return filteredYearRows;
  return filteredMonthRows;
}

export function rangeNeedsYearData(range) {
  return range === "monthly" || range === "yearly";
}
